import { Component, OnInit, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import MetisMenu from 'metismenujs';
import { Router, NavigationEnd } from '@angular/router';
import { MENU } from './menu';
import { MenuItem } from './menu.model';

import { TranslateService } from '@ngx-translate/core';
import { Constants, PAGE_NAMES, ROLES } from '../../core/helpers/constants';
import { AppService } from '../../store/app.service';
import * as _ from 'underscore';
import { environment } from '../../../environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { InstanceService } from '../../core/instance/instance.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})

/**
 * Sidebar Component
 */
export class SidebarComponent implements OnInit {
  private readonly menuRouteAliases: Array<{ prefix: string; target: string }> = [
    { prefix: '/pages/campaign-details/', target: '/pages/campaigns' },
    { prefix: '/pages/edit-campaign', target: '/pages/campaigns' },
    { prefix: '/pages/reports/rollup-campaigns/', target: '/pages/reports' },
    { prefix: '/pages/reports/rollup-report/viewer/', target: '/pages/reports' },
    { prefix: '/pages/reports/view-report/', target: '/pages/reports' },
    { prefix: '/pages/client-details/', target: '/pages/clients' },
    { prefix: '/pages/location-details/', target: '/pages/locations' },
    { prefix: '/pages/prospect-details/', target: '/pages/prospects' },
    { prefix: '/pages/archived-location-details/', target: '/pages/archived-locations' },
    { prefix: '/pages/lead-details/', target: '/pages/leads' },
    { prefix: '/pages/orders/details/', target: '/pages/orders' },
    { prefix: '/pages/shop/cart/', target: '/pages/shop' },
    { prefix: '/pages/shop/ordercomplete/', target: '/pages/shop' },
    { prefix: '/pages/edit-coupon', target: '/pages/coupons' }
  ];
  private readonly logoAssetByInstance: Record<string, { path: string; cssClass: string }> = {
    ffm: { path: 'assets/images/logo/FFM-White_logo.svg', cssClass: 'sidebar-brand-logo__svg--ffm' },
    prspr: { path: 'assets/images/logo/PRSPR_Logo_Black.svg', cssClass: 'sidebar-brand-logo__svg--prspr' },
    cpjp: { path: 'assets/images/logo/KommunityLogo.svg', cssClass: 'sidebar-brand-logo__svg--cpjp' }
  };

  @ViewChild('sideMenu') sideMenu!: ElementRef;
  menu: any;
  menuItems: MenuItem[] = [];
  userRoles: any[] = []
  toggleLogo = true;
  titleLogoText: string = 'FFM';
  activeLogoSvg: SafeHtml | null = null;
  activeLogoCssClass = '';

  @Output() mobileMenuButtonClicked = new EventEmitter<void>();

  constructor(
    private router: Router,
    private sanitizer: DomSanitizer,
    public translate: TranslateService,
    public appService: AppService,
    private _cookieService: CookieService,
    private instanceService: InstanceService
  ) {
    translate.setDefaultLang('en');
    router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        this._activateMenuDropdown();
        this.closeMobileMenuOnNavigation();
      }
    });
  }

  ngOnInit(): void {
    this.syncInstanceBranding();
    this.loadActiveLogo();
    this.userRoles = this.appService.getActiveUserRoles$().value
    if (this.userRoles.find(x => x.code == ROLES.Admin.Code))
      this.menuItems = MENU;
    else {
      var mergedRoles: any[] = [];
      for (var r of this.userRoles) {
        for (var p of r.perms) {
          mergedRoles.push(p.pageName)
        }
      }
      this.menuItems = MENU.filter(m => mergedRoles.includes(m.pageName))
      if (this.userRoles.find(x => ((x.code == 5) || (x.code == 6) || (x.code == 7) || (x.code == 8) || (x.code == 9))) != undefined) {
        this.menuItems.push(MENU.find(x => x.pageName == 'Partners')!);
        this.menuItems.push(MENU.find(x => x.pageName == 'FAQ')!);
        this.menuItems = this.menuItems.filter(m => m.pageName && !['Subscriptions', 'Orders', 'Users'].includes(m.pageName));
        if (this.userRoles.find(x => ((x.code == 5) || (x.code == 6) || (x.code == 8))) != undefined) {
          if (!this.menuItems.some(item => item.pageName === 'MyAccount')) {
            this.menuItems.push(MENU.find(x => x.pageName == 'MyAccount')!);
          }
        }
      }
      this.menuItems.push(MENU.find(x => x.name == 'logout')!)
    }
    this.menuItems = _.sortBy(this.menuItems, 'id');

    const hiddenPages = new Set(this.instanceService.getConfig().features.hiddenPageNames || []);
    if (hiddenPages.size) {
      this.menuItems = this.menuItems.filter(m => !m.pageName || !hiddenPages.has(m.pageName));
    }

    if (this.instanceService.is('cpjp')) {
      this.menuItems = this.menuItems.map(item => {
        if (item.pageName === undefined || !item.link) return item;

        if (item.pageName === PAGE_NAMES.Clients) {
          return { ...item, link: '/pages/locations' };
        }
        if (item.pageName === PAGE_NAMES.Prospects) {
          return { ...item, link: '/pages/archived-locations' };
        }
        return item;
      });
    }
  }

  private syncInstanceBranding(): void {
    switch (this.instanceService.getKey()) {
      case 'prspr':
        this.titleLogoText = 'PRSPR';
        break;
      case 'cpjp':
        this.titleLogoText = 'CPJP';
        break;
      case 'ffm':
      default:
        this.titleLogoText = 'FFM';
        break;
    }
  }

  /**
   * Initialize
   */
  initialize(): void {
    this.menuItems = MENU;
  }

  /***
   * Activate droup down set 
   */
  ngAfterViewInit() {
    this.menu = new MetisMenu('#side-menu');
    this._activateMenuDropdown();
  }

  /**
   * Returns true or false if given menu item has child or not
   * @param item menuItem
   */
  hasItems(item: MenuItem) {
    return item.subItems !== undefined ? item.subItems.length > 0 : false;
  }

  /**
   * remove active and mm-active class
   */
  _removeAllClass(className: any) {
    const els = document.getElementsByClassName(className);
    while (els[0]) {
      els[0].classList.remove(className);
    }
  }

  /**
 * Logout the user
 */
  doLogout() {
    this._cookieService.delete('AuthToken', '/', environment.cookieDomain);
    this._cookieService.delete('FfmUserId', '/', environment.cookieDomain);
    this._cookieService.delete('PrsprUserId', '/', environment.cookieDomain);
    this._cookieService.delete('CpjpUserId', '/', environment.cookieDomain);
    this._cookieService.delete('CurrentInstance', '/', environment.cookieDomain);
    const preservedLang = localStorage.getItem(Constants.LS_LANGUAGE);
    const preservedThemeMode = localStorage.getItem(Constants.LS_THEME_MODE);
    const preservedSidebarSize = localStorage.getItem(Constants.LS_SIDEBAR_SIZE);
    localStorage.clear();
    if (preservedLang) {
      localStorage.setItem(Constants.LS_LANGUAGE, preservedLang);
    }
    if (preservedThemeMode) {
      localStorage.setItem(Constants.LS_THEME_MODE, preservedThemeMode);
    }
    if (preservedSidebarSize) {
      localStorage.setItem(Constants.LS_SIDEBAR_SIZE, preservedSidebarSize);
    }
    this.appService.clearData();
    const loginUrl = new URL(environment.loginFrontendUrl, window.location.href);
    if (preservedLang) {
      loginUrl.searchParams.set('lang', preservedLang);
    }
    const inst = (localStorage.getItem(Constants.LS_CURRENT_INSTANCE) || '').toLowerCase();
    if (inst === 'ffm' || inst === 'prspr' || inst === 'cpjp') {
      loginUrl.searchParams.set('inst', inst);
    }
    window.location.href = loginUrl.href;
  }

  menuClicked(name?: string) {
    if (name === "logout") {
      this.doLogout();
    }
  }

  toggleMobileMenu(event: Event) {
    event.preventDefault();
    this.mobileMenuButtonClicked.emit();
    if (window.screen.width >= 992) {
      this.toggleLogo = !this.isSidebarCollapsed();
    } else {
      this.toggleLogo = !this.toggleLogo;
    }
  }

  isSidebarCollapsed(): boolean {
    return document.body.getAttribute('data-sidebar-size') === 'sm';
  }

  // Method to check if the user has any of the specified roles (External Users)
  hasSpecialRoles(): boolean {
    return this.userRoles.some(x => [5, 6, 7, 8, 9].includes(x.code));
  }

  /**
   * Activate the parent dropdown
   */
  _activateMenuDropdown() {
    this._removeAllClass('active');
    this._removeAllClass('mm-active');
    this._removeAllClass('mm-show');
    const links: any = document.querySelectorAll('#side-menu .side-nav-link-ref:not(.side-nav-action-link)');
    let menuItemEl = null;
    // tslint:disable-next-line: prefer-for-of
    const paths = [];
    for (let i = 0; i < links.length; i++) {
      paths.push(links[i]['pathname']);
    }
    const resolvedPath = this.resolveMenuPath(window.location.pathname);
    var itemIndex = paths.indexOf(resolvedPath);
    if (itemIndex === -1) {
      const strIndex = resolvedPath.lastIndexOf('/');
      const item = resolvedPath.substr(0, strIndex).toString();
      menuItemEl = links[paths.indexOf(item)];
    } else {
      menuItemEl = links[itemIndex];
    }
    if (menuItemEl) {
      menuItemEl.classList.add('active');
      const parentEl = menuItemEl.parentElement;
      if (parentEl) {
        parentEl.classList.add('mm-active');
        const parent2El = parentEl.parentElement.closest('ul');
        if (parent2El && parent2El.id !== 'side-menu') {
          parent2El.classList.add('mm-show');
          const parent3El = parent2El.parentElement;
          if (parent3El && parent3El.id !== 'side-menu') {
            parent3El.classList.add('mm-active');
            const childAnchor = parent3El.querySelector('.has-arrow');
            const childDropdown = parent3El.querySelector('.has-dropdown');
            if (childAnchor) {
              childAnchor.classList.add('mm-active');
            }
            if (childDropdown) {
              childDropdown.classList.add('mm-active');
            }
            const parent4El = parent3El.parentElement;
            if (parent4El && parent4El.id !== 'side-menu') {
              parent4El.classList.add('mm-show');
              const parent5El = parent4El.parentElement;
              if (parent5El && parent5El.id !== 'side-menu') {
                parent5El.classList.add('mm-active');
                const childanchor = parent5El.querySelector('.is-parent');
                if (childanchor && parent5El.id !== 'side-menu') {
                  childanchor.classList.add('mm-active');
                }
              }
            }
          }
        }
      }
    }
  }

  private resolveMenuPath(pathname: string): string {
    const alias = this.menuRouteAliases.find(routeAlias => pathname.startsWith(routeAlias.prefix));
    return alias?.target ?? pathname;
  }

  private closeMobileMenuOnNavigation(): void {
    if (window.innerWidth >= 992) {
      return;
    }

    document.body.classList.remove('sidebar-enable');
    this.toggleLogo = true;
  }

  private loadActiveLogo(): void {
    const logoConfig = this.logoAssetByInstance[this.instanceService.getKey()];

    if (!logoConfig) {
      this.activeLogoSvg = null;
      this.activeLogoCssClass = '';
      return;
    }

    this.activeLogoCssClass = logoConfig.cssClass;

    fetch(new URL(logoConfig.path, document.baseURI).toString())
      .then(response => {
        if (!response.ok) {
          throw new Error(`Unable to load logo asset: ${logoConfig.path}`);
        }

        return response.text();
      })
      .then(svgMarkup => {
        this.activeLogoSvg = this.sanitizer.bypassSecurityTrustHtml(svgMarkup);
      })
      .catch(() => {
        this.activeLogoSvg = null;
      });
  }
}
